import { ParsedUrlQuery } from 'querystring';

import { useState, useEffect, useRef, useMemo } from 'react';

import { useInView } from 'react-intersection-observer';
import { useParams, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import moment from 'moment';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { throttle } from 'lodash-es';
import Cookies from 'js-cookie';

import Tab from '@components/ui/scroll-tab/ScrollTab';
import { useSearchURL } from '@api/data-loaders/resource';
import { ScrollTabWrapper } from '@components/ui/scroll-tab/styled';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import { StickyLogoBox } from '@components/views/hotel/listing/HotelListingStyle';
import { useScroll, useScrollDirection } from '@hooks/hooks';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import {
  convertDateToMomentObject,
  HOTEL_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';
import ResponsiveListingBox from '@components/views/hotel/listing/utils/ResponsiveListingBox';
const SearchBar = dynamic(
  () => import('@components/views/hotel/components/SearchBar/SearchBar'),
  { ssr: true }
);
import { convertQueryRoomToHotelRooms } from '@components/views/hotel/utils';
import { getOffset } from '@utils/getElementOffset';
import {
  HotelDetailViewModel,
  WebUrlType,
  PackageHotelDetailViewModel,
  HotelDetailReviewViewModel,
} from '@api/v3';
import { useDevice } from '@contexts/device';
import SearchReservedId from '@components/ui/SearchReservedId';
import { paramsToObject } from '@components/views/utils';
import { getRoomsFromCookie } from '@components/views/landing/components/bookerNext/Booker';

import { useSetSelectedVariations } from '../../context';
import {
  DetailSearchWrapper,
  DetailSearchBarContentWrapper,
  SearchBarWrapper,
  InVisibleSearchBatPlaceHolder,
} from './styled';
import { getTextFromSlug } from './helpers';

export const DetailSearchBox = ({
  detail,
  selectedTab,
  setSelectedTab,
  setSearchLoading,
}: {
  detail?: boolean;
  selectedTab?: string;
  setSearchLoading?: (val: boolean) => void;
  setSelectedTab?: (string: any) => void;
}) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return searchParams ? paramsToObject(searchParams.entries()) : {};
  }, [searchParams]);
  const { in: startDate, out: endDate, isPackage } = query;
  const {
    location,
    hotelRoomsList,
    setHotelDateSelection,
    isHotelDateSelected,
  } = useSearchBarContext();
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const setSelectedVariations = useSetSelectedVariations();

  // ======================================
  // should shallow push if we the target page is hotel detail
  // ======================================
  const shouldShallowPush = useMemo(() => {
    if (isPackage) return false;
    return location?.type === WebUrlType.HOTEL;
  }, [location]);

  return (
    <ResponsiveListingBox
      detail={detail}
      suggestionLocation={String(params?.slug)}
      startDate={startDate ? moment(startDate) : null}
      endDate={endDate ? moment(endDate) : null}
      hotelRoomsList={hotelRoomsList}
      isModalVisible={showSearchModal || isHotelDateSelected}
      toggleIsModalVisible={() => setShowSearchModal((val) => !val)}
      shouldShallowPush={shouldShallowPush}
      hideModal={() => {
        setShowSearchModal(false);
        setHotelDateSelection(false);
      }}
      location={location}
      onResearchClick={() => {
        setSearchLoading?.(true);
        setSelectedVariations([]);
      }}
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
    />
  );
};

const HotelSearchBar = ({
  onResearch,
  data,
  ratingDetail,
  withPackageTab,
  selectedTab,
  setSearchLoading,
}: {
  onResearch: () => void;
  data?: HotelDetailViewModel | PackageHotelDetailViewModel;
  ratingDetail?: HotelDetailReviewViewModel;
  withPackageTab?: boolean;
  selectedTab?: boolean;
  setSearchLoading?: (val: boolean) => void;
}) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return searchParams ? paramsToObject(searchParams.entries()) : {};
  }, [searchParams]);
  const { _in, in: inParam, out: endDate, isPackage } = query;
  const startDate = _in ?? inParam;
  const localRoomsStr = Cookies.get('localRooms');
  const localRooms: ListingSearchRoomInterface[] = localRoomsStr
    ? JSON.parse(localRoomsStr)
    : [];
  const { y } = useScroll();
  const direction = useScrollDirection();
  const { isDesktop } = useDevice();
  const tabsWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isActiveRooms, setIsActiveRooms] = useState<boolean>(false);
  const [activeTab, steActiveTab] = useState<Element | null>(null);

  const {
    location,
    hotelRoomsList,
    startDate: checkInDate,
    endDate: checkOutDate,
    setDates,
    setLocation,
    setIsFetchedItems,
    setIsStickySearch,
    isStickySearch,
    setHotelRoomsList,
    isLocationEntered,
    setIsLocationEntered,
    setHotelDateSelection,
    isHotelDateSelected,
  } = useSearchBarContext();
  const { ref: searchBarRef, inView: isSearchBarInView } = useInView({
    threshold: 1,
  });
  const tabsInfo = useMemo(() => {
    const tabs = [
      { key: 'overview', title: 'Genel Bakış', show: true },
      {
        key: 'rooms',
        title: 'Odalar',
        show: data?.rooms && data?.rooms.length > 0 ? true : false,
      },
      {
        key: 'features',
        title: 'Özellikler',
        show: data?.features && data?.features?.length > 0 ? true : false,
      },
      {
        key: 'rules',
        title: 'Kurallar',
        show:
          data?.checkinBeginTime && data?.checkinBeginTime.length > 0
            ? true
            : false,
      },
      {
        key: 'about',
        title: 'Hakkında',
        show: data?.description && data?.description.length > 0 ? true : false,
      },

      {
        key: 'announcements',
        title: 'Duyurular',
        show:
          data?.announcements && data?.announcements?.length > 0 ? true : false,
      },
      {
        key: 'location',
        title: 'Konum',
        show: data?.latitude && data?.longitude ? true : false,
      },
      {
        key: 'scoring',
        title: 'Puan ve Yorumlar',
        show: ratingDetail?.rating ? true : false,
      },
    ];
    return tabs;
  }, [data, ratingDetail]);

  const [isScrollingMenuOpen, setIsScrollingMenuOpen] =
    useState<boolean>(false);
  const [hotelRooms, setHotelRooms] =
    useState<ListingSearchRoomInterface[]>(getRoomsFromCookie);
  const { text: searchText } = useSearchURL();
  const [locationInputVal, setLocationInputVal] = useState<string>(searchText);
  useEffect(() => {
    setLocationInputVal(getTextFromSlug((params?.slug as string) ?? ''));

    setDates({
      startDate: startDate
        ? convertDateToMomentObject(
            startDate as string,
            HOTEL_SEARCH_DATE_FORMAT
          )
        : null,
      endDate: endDate
        ? convertDateToMomentObject(endDate as string, HOTEL_SEARCH_DATE_FORMAT)
        : null,
    });
  }, [query]);

  // ====================================
  // scroll the active tab when going out of the viewport
  // ===================================

  const handleActiveScrollTab = () => {
    const targetTab = tabsWrapperRef.current?.children
      .item(0)
      ?.querySelector('.active');

    if (targetTab) {
      steActiveTab(targetTab);
    }
    if (window.scrollY > 700) {
      setIsStickySearch(true);
    } else {
      setIsStickySearch(false);
    }
  };

  useEffect(() => {
    if (activeTab) {
      const isInView =
        getOffset(activeTab).left + 0 >= 0 &&
        getOffset(activeTab).left - 0 <= window.innerWidth;

      if (isInView) return;
      tabsWrapperRef.current?.children.item(0)?.scrollTo({
        left: getOffset(activeTab).left,
        top: getOffset(activeTab).top,
        behavior: 'smooth',
      });
    }
  }, [activeTab]);

  useEffect(() => {
    window.addEventListener('scroll', throttle(handleActiveScrollTab, 200), {
      passive: true,
    });
    return () => {
      window.removeEventListener('scroll', handleActiveScrollTab);
    };
  }, []);

  // ======================================
  // should shallow push if we the target page is hotel detail
  // ======================================
  const shouldShallowPush = useMemo(() => {
    if (isPackage) return false;
    return location?.type === WebUrlType.HOTEL;
  }, [location]);

  // ===========================
  // Show and Hide elements base on the scroll event
  // ===========================
  useEffect(() => {
    if (y && y > 800 && isDesktop) {
      setIsScrollingMenuOpen(true);
    } else if (y && y > 100 && !isDesktop) {
      setIsScrollingMenuOpen(true);
    } else {
      setIsScrollingMenuOpen(false);
    }
  }, [y]);

  // ================================================
  // Get items from local storage and update search bar rooms
  // ================================================
  useEffect(() => {
    const roomsFromQuery = convertQueryRoomToHotelRooms(query.room as string);

    if (roomsFromQuery?.length > 0) {
      setHotelRoomsList(roomsFromQuery);
      setHotelRooms(roomsFromQuery);
    } else if (localRooms?.length > 0) {
      // query.room yoksa veya boşsa cookie'den al
      setHotelRoomsList(localRooms);
      setHotelRooms(localRooms);
    }
  }, [query]);

  // ================================================
  // Check if the active section is rooms
  // ================================================
  useEffect(() => {
    if (isScrollingMenuOpen) {
      const link =
        tabsWrapperRef.current?.children[0].querySelector('a.active.rooms');

      if (link) return setIsActiveRooms(true);
      setIsActiveRooms(false);
    }
  }, [y]);

  const showStickySearchBar = isStickySearch && direction === 'up';
  return (
    <>
      <ScrollTabWrapper
        shouldResearchPackage={false}
        visible={isScrollingMenuOpen}
        direction={direction}
        isStickySearch={isSearchBarInView}
        ref={tabsWrapperRef}
        extraHeight={isActiveRooms}
        selectedTabHotel={selectedTab}
      >
        <SearchReservedId show={isScrollingMenuOpen && direction === 'down'} />

        <Tab tabs={tabsInfo} />
      </ScrollTabWrapper>
      {isDesktop ? (
        <>
          <DetailSearchWrapper
            sticky={isStickySearch}
            withPackageTab={withPackageTab}
            animated={showStickySearchBar}
            ref={searchBarRef}
          >
            <DetailSearchBarContentWrapper>
              {isStickySearch ? (
                <StickyLogoBox>
                  <Link href='/' prefetch={false}>
                    <Image
                      alt='setur logo'
                      src='/static/images/misc-setur-logo-s.svg'
                      width={22}
                      height={30}
                    />
                  </Link>
                </StickyLogoBox>
              ) : null}

              <SearchBarWrapper>
                <SearchBar
                  onResearchClick={() => {
                    onResearch();
                  }}
                  setSearchLoading={setSearchLoading}
                  location={location}
                  setDates={setDates}
                  startDate={checkInDate}
                  endDate={checkOutDate}
                  hotelRoomsList={hotelRoomsList}
                  setHotelRoomsList={setHotelRoomsList}
                  initialHotelRooms={hotelRooms}
                  setInitialHotelRooms={setHotelRooms}
                  isLocationEntered={isLocationEntered}
                  setIsLocationEntered={setIsLocationEntered}
                  setIsFetchedItems={setIsFetchedItems}
                  isStickySearch={isStickySearch}
                  locationInputVal={locationInputVal}
                  setLocation={setLocation}
                  isDateSelected={isHotelDateSelected}
                  setIsDateSelected={setHotelDateSelection}
                  setLocationInputVal={setLocationInputVal}
                  shouldShallowPush={shouldShallowPush}
                />
              </SearchBarWrapper>
            </DetailSearchBarContentWrapper>
          </DetailSearchWrapper>
          {showStickySearchBar ? <InVisibleSearchBatPlaceHolder /> : null}
        </>
      ) : null}
    </>
  );
};

export default HotelSearchBar;
